import { queryClient } from '@/main';
import appServices from '@/services/appServices';
import { AppTablesTypeMap } from '@polybank/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export default function useAllRows<K extends keyof AppTablesTypeMap>(
  tableName: K,
  options?: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }
) {
  const { sortBy, sortOrder } = options ?? {};
  const data = useQuery({
    queryKey: ['all-rows', tableName, sortBy, sortOrder],
    queryFn: () => appServices.admin.listAllRows(tableName, sortBy, sortOrder),
  });

  const rowsMap = useMemo(() => {
    if (!data.data?.length) {
      return {};
    }
    return data.data.reduce(
      (acc, curr) => {
        acc[curr.$id!] = curr;
        return acc;
      },
      {} as Record<string, AppTablesTypeMap[K]>
    );
  }, [data.data]);

  const addItem = useCallback(
    (item: AppTablesTypeMap[K]) => {
      queryClient.setQueryData(
        ['all-rows', tableName, sortBy, sortOrder],
        (oldData: AppTablesTypeMap[K][] | undefined) => [
          ...(oldData ?? []),
          item,
        ]
      );
    },
    [tableName, sortBy, sortOrder]
  );
  const updateItem = useCallback(
    (item: AppTablesTypeMap[K]) => {
      queryClient.setQueryData(
        ['all-rows', tableName, sortBy, sortOrder],
        (oldData: AppTablesTypeMap[K][] | undefined) =>
          (oldData ?? []).map((i) => (i.$id === item.$id ? item : i))
      );
    },
    [tableName, sortBy, sortOrder]
  );
  const removeItem = useCallback(
    (item: AppTablesTypeMap[K]) => {
      queryClient.setQueryData(
        ['all-rows', tableName, sortBy, sortOrder],
        (oldData: AppTablesTypeMap[K][] | undefined) =>
          (oldData ?? []).filter((i) => i.$id !== item.$id)
      );
    },
    [tableName, sortBy, sortOrder]
  );

  const result = useMemo(() => {
    return {
      data: { rows: data.data ?? [] },
      rowsMap,
      addItem,
      updateItem,
      removeItem,
      isLoading: data.isLoading,
    };
  }, [data.data, data.isLoading, rowsMap, addItem, updateItem, removeItem]);

  return result;
}
